<template>
    <div>
        <div class="row mb-2">
            <div class="col-12">
                <label>
                    {{resource('JourneyComposer_BrickSettingsTouchpoint_Custom_Conditions_Header')}}
                </label>
            </div>
        </div>
        
        <div class="row mb-2">
            <div class="col-12">
                <button class="btn btn-outline-secondary" @click="addCondition()" :disabled="!canAddNewConditions">
                    <i class="fas fa-plus"></i> {{resource('Button_Add')}}
                </button>
            </div>
        </div>
        
        <div class="row mb-2" v-if="isLoading">
            <i class="loader fas fa-spinner fa-spin fa-2x"></i>
        </div>
        
        <template v-if="fields.length !== 0">
            <brick-custom-condition-field v-for="(condition, index) in sortedValue"
                ref="fields"
                :key="condition.id"
                                          
                :value="condition"
                :available-types="getSelectableTypes(condition.type)"
                :is-selectable-dropdown-value="isSelectableDropdownValue"
                                          
                @input="updateCondition($event, value.indexOf(condition))"
                @delete="removeCondition(condition)"
            />
        </template>
    </div>
</template>

<script>
import {guid} from "@core/Util/guid";

import resourceMixin from '@core/Util/Mixins/resources';
import permissionsMixin from '@modules/JourneyBuilder/Mixins/journey-permissions';

import BrickCustomConditionField from "./BrickCustomConditionField";

export default {
    name: "BrickCustomConditions",
    
    components: {
        BrickCustomConditionField
    },
    
    mixins: [resourceMixin, permissionsMixin],
    
    props: {
        /** @type {JourneyBuilder.CustomField[]} */
        fields: {
            type: Array,
            required: true
        },
        
        /** @type { {id: String, type: String, value: String}[] } */
        value: {
            type: Array,
            required: true
        },
        
        /** @type { function(dropdownValue: JourneyBuilder.CustomFieldDropdownValue):Boolean } */
        isSelectableDropdownValue: {
            type: Function,
            required: false,
            default() {
                return () => true;
            }
        },
        
        isLoading: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    
    computed: {
        sortedValue() {
            //For display - sort our values by the associated type being required
            //Required types should always be displayed on top for a consistent look
            const requiredTypeMap = Object.values(this.fields).reduce((map, type) => {
                map[type.name] = type.isRequired;
                return map;
            });
            
            return [...this.value].sort((left, right) => {
                if(requiredTypeMap[left.type]) {
                    return requiredTypeMap[right.type] ? 0 : -1;
                }
                
                return requiredTypeMap[right.type] ? 1 : 0;
            });
        },
        
        canAddNewConditions() {
            return this.canWrite &&
                this.getSelectableTypes(undefined).length !== 0;
        }
    },
    
    watch: {
        fields(newValue) {
            let conditions = [...this.value];
            
            //When not loading, remove all field values no longer available
            if(!this.isLoading) {
                const fieldNames = newValue.map(field => field.name);

                conditions = conditions.filter(condition => fieldNames.includes(condition.type));
            }
            
            //Ensure all required types have a matching condition
            newValue.forEach(field => {
                if(
                    field.isRequired &&
                    !conditions.some(condition => condition.type === field.name)
                ) {
                    conditions.push(this.createCondition(field));
                }
            });

            //Only emit a single time to prevent accidental loss of newly added values
            this.$emit(
                'input',
                conditions
            );
        },
        
        value() {
            //Wait for UI to render
            this.$nextTick(
                () => this.updateValidationState()
            );
        }
    },
    
    methods: {
        /**
         * @param {JourneyBuilder.CustomField} type
         * @return {{id, type, value: string}}
         */
        createCondition: (type) => ({
            id: guid(),
            type: type.name,
            value: type.defaultValue || ''
        }),
        
        addCondition() {
            //When no more free types are available, new conditions cannot be added anymore. Thus we always have at least a single item in our selectableTypes array.
            const type = this.getSelectableTypes(undefined)[0];

            this.$emit('input', [
                ...this.value,
                this.createCondition(type)
            ]);
        },

        updateCondition(condition, index) {
            const newValue = [...this.value];
            newValue[index] = condition;
            
            this.$emit('input', newValue);
        },
        
        removeCondition(condition) {
            //We assume only valid values are passed in
            const newValue = [...this.value];
            newValue.splice(newValue.indexOf(condition), 1);
            
            this.$emit('input', newValue);
        },

        getSelectableTypes(currentlySelectedType) {
            let selectedTypes = this.value.map(v => v.type);

            //Remove current type - current type should also be selectable
            if(currentlySelectedType){
                //Filter to not break legacy configurations containing multiple fields of the same type
                selectedTypes = selectedTypes.filter(t => t !== currentlySelectedType);
            }
            
            return this.fields.filter(field => !selectedTypes.includes(field.name));
        },

        updateValidationState(){
            let isValid = true;

            //We can't know if we're valid if our fields haven't loaded yet - assume valid for the time being
            if(this.$refs.fields){
                isValid = this.$refs.fields.every(field => field.isValid);
            }

            this.$emit('validation-state-changed', isValid);
        },

        triggerValidations() {
            (this.$refs.fields || []).forEach(field => field.triggerValidation());
        }
    }
}
</script>

<style scoped>
.loader {
    position: relative;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
}
</style>